// Excellence Index scoring functions

import type {
    ExcellenceQuestionType,
    ExcellenceScores,
    ExcellenceDimension,
    ExcellenceScenario
} from '../types/surveys/ExcellenceSurvey.ts'
import { ExcellenceDimension as Dimension } from '../types/surveys/ExcellenceSurvey.ts'

// Weight for scenarios in scoring (scenarios reveal actual behavior, so weighted more heavily)
const SCENARIO_WEIGHT = 1.5

/**
 * Convert scenario level response to numeric score (1-5 scale)
 * low = 1, medium = 3, high = 5
 */
function levelToScore(level: 'low' | 'medium' | 'high'): number {
    switch (level) {
        case 'low': return 1
        case 'medium': return 3
        case 'high': return 5
    }
}

/**
 * Calculate Excellence scores from answered questions
 * Returns scores normalized to 0-100 scale
 */
export function calculateExcellenceScores(questions: ExcellenceQuestionType[]): ExcellenceScores {
    const scores: ExcellenceScores = {
        [Dimension.ADAPTABILITY]: 0,
        [Dimension.PERSEVERANCE]: 0,
        [Dimension.LEARNING_AGILITY]: 0,
        [Dimension.QUALITY_STANDARDS]: 0,
        [Dimension.CONTINUOUS_IMPROVEMENT]: 0
    }

    // Group questions by dimension
    const dimensionQuestions: Record<ExcellenceDimension, ExcellenceQuestionType[]> = {
        [Dimension.ADAPTABILITY]: [],
        [Dimension.PERSEVERANCE]: [],
        [Dimension.LEARNING_AGILITY]: [],
        [Dimension.QUALITY_STANDARDS]: [],
        [Dimension.CONTINUOUS_IMPROVEMENT]: []
    }

    questions.forEach(q => {
        if (q.value !== undefined) {
            dimensionQuestions[q.dimension].push(q)
        }
    })

    // Calculate score for each dimension
    Object.keys(dimensionQuestions).forEach(dimension => {
        const dimKey = dimension as ExcellenceDimension
        const dimQs = dimensionQuestions[dimKey]

        if (dimQs.length === 0) {
            scores[dimKey] = 0
            return
        }

        let sum = 0
        dimQs.forEach(q => {
            if (q.value !== undefined) {
                // Reverse scoring for reversed questions
                const score = q.reversed ? (6 - q.value) : q.value
                sum += score
            }
        })

        // Normalize to 0-100 scale
        const rawScore = sum
        const minPossible = dimQs.length * 1
        const maxPossible = dimQs.length * 5

        scores[dimKey] = Math.round(((rawScore - minPossible) / (maxPossible - minPossible)) * 100)
    })

    return scores
}

/**
 * Calculate Excellence scores including both Likert questions AND behavioral scenarios
 * Scenarios are weighted more heavily (1.5x) as they reveal actual behavioral responses
 * Returns scores normalized to 0-100 scale
 */
export function calculateCombinedExcellenceScores(
    questions: ExcellenceQuestionType[],
    scenarios?: ExcellenceScenario[]
): ExcellenceScores {
    const scores: ExcellenceScores = {
        [Dimension.ADAPTABILITY]: 0,
        [Dimension.PERSEVERANCE]: 0,
        [Dimension.LEARNING_AGILITY]: 0,
        [Dimension.QUALITY_STANDARDS]: 0,
        [Dimension.CONTINUOUS_IMPROVEMENT]: 0
    }

    const weights: Record<ExcellenceDimension, number> = {
        [Dimension.ADAPTABILITY]: 0,
        [Dimension.PERSEVERANCE]: 0,
        [Dimension.LEARNING_AGILITY]: 0,
        [Dimension.QUALITY_STANDARDS]: 0,
        [Dimension.CONTINUOUS_IMPROVEMENT]: 0
    }

    // Score Likert questions (weight = 1)
    questions.forEach(q => {
        if (q.value !== undefined) {
            // Reverse scoring for reversed questions
            const score = q.reversed ? (6 - q.value) : q.value
            scores[q.dimension] += score
            weights[q.dimension] += 1
        }
    })

    // Score scenarios (weight = SCENARIO_WEIGHT, typically 1.5)
    if (scenarios && scenarios.length > 0) {
        scenarios.forEach(s => {
            if (s.selectedOptionId) {
                const selectedOption = s.options.find(opt => opt.id === s.selectedOptionId)
                if (selectedOption) {
                    const score = levelToScore(selectedOption.level)
                    scores[s.dimension] += score * SCENARIO_WEIGHT
                    weights[s.dimension] += SCENARIO_WEIGHT
                }
            }
        })
    }

    // Calculate weighted averages and normalize to 0-100 scale
    const normalizedScores: ExcellenceScores = {} as ExcellenceScores
    Object.entries(scores).forEach(([dimension, weightedSum]) => {
        const dimKey = dimension as ExcellenceDimension
        const weight = weights[dimKey]

        if (weight > 0) {
            // Average score is weightedSum / weight (on 1-5 scale)
            const averageScore = weightedSum / weight
            // Normalize to 0-100: (score - 1) / 4 * 100
            normalizedScores[dimKey] = Math.round(((averageScore - 1) / 4) * 100)
        } else {
            normalizedScores[dimKey] = 0
        }
    })

    return normalizedScores
}

/**
 * Calculate overall excellence score
 */
export function calculateOverallExcellenceScore(scores: ExcellenceScores): number {
    const values = Object.values(scores)
    const sum = values.reduce((acc, score) => acc + score, 0)
    return Math.round(sum / values.length)
}

/**
 * Get interpretation for an excellence dimension score
 */
export function interpretExcellenceScore(dimension: ExcellenceDimension, score: number): string {
    const level = score >= 70 ? 'high' : score >= 30 ? 'medium' : 'low'

    const interpretations: Record<ExcellenceDimension, Record<string, string>> = {
        [Dimension.ADAPTABILITY]: {
            high: 'Máš vysokú adaptabilitu. Dokážeš sa rýchlo prispôsobiť novým situáciám a zmeny vnímáš ako príležitosť. V neistých podmienkach si flexibilný/flexibilná.',
            medium: 'Máš priemernú adaptabilitu. V niektorých situáciách sa prispôsobíš ľahko, v iných preferuješ stabilitu a predvídateľnosť.',
            low: 'Zmeny ťa môžu stresovať a adaptácia na nové situácie ti trvá dlhšie. Skús začať s malými zmenami a postupne budovať flexibilitu.'
        },
        [Dimension.PERSEVERANCE]: {
            high: 'Máš vysokú vytrvalosť. Dokážeš vytrvať pri náročných úlohách a prekážky ťa neodrádzajú od tvojich cieľov.',
            medium: 'Máš priemernú vytrvalosť. Niekedy dokážeš vytrvať, ale pri veľkých prekážkach môžeš stratiť motiváciu.',
            low: 'Máš nižšiu vytrvalosť. Môže ťa ľahko odradiť zložitosť alebo dĺžka úlohy. Skús si nastaviť menšie medziciele a oslavovať drobné úspechy.'
        },
        [Dimension.LEARNING_AGILITY]: {
            high: 'Máš vysokú schopnosť učiť sa. Rýchlo si osvojuješ nové poznatky a dokážeš ich aplikovať v rôznych kontextoch.',
            medium: 'Máš priemernú schopnosť učiť sa. Nové veci sa učíš priebežne, ale niekedy ti to môže trvať dlhšie.',
            low: 'Učenie nových vecí ti môže trvať dlhšie. Skús experimentovať s rôznymi metódami učenia a nájsť to, čo ti najviac vyhovuje.'
        },
        [Dimension.QUALITY_STANDARDS]: {
            high: 'Máš vysoké štandardy kvality. Dbáš na detaily a snažíš sa podávať výkony na najvyššej úrovni.',
            medium: 'Máš primerané štandardy kvality. Dbáš na kvalitu, keď je to dôležité, ale nie si príliš perfekcionistický/perfekcionistická.',
            low: 'Máš nižšie štandardy kvality alebo si menej detailne orientovaný/orientovaná. Skús sa zamyslieť nad tým, kde by vyššia kvalita mohla priniesť lepšie výsledky.'
        },
        [Dimension.CONTINUOUS_IMPROVEMENT]: {
            high: 'Si orientovaný/orientovaná na neustále zlepšovanie. Pravidelne hľadáš spôsoby, ako byť lepší/lepšia a efektívnejší/efektívnejšia.',
            medium: 'Občas premýšľaš o zlepšení, ale nie je to tvoja priorita. Niekedy si spokojný/spokojná so status quo.',
            low: 'Neustále zlepšovanie nie je tvoja priorita. Si spokojný/spokojná s tým, ako veci robíš. Skús si uvedomiť, že malé zmeny môžu priniesť veľké výsledky.'
        }
    }

    return interpretations[dimension][level] || ''
}

/**
 * Get overall excellence interpretation
 */
export function interpretOverallExcellence(overallScore: number): {
    level: string
    description: string
    strengths: string[]
    developmentAreas: string[]
} {
    if (overallScore >= 70) {
        return {
            level: 'Vysoká orientácia na excelenciu',
            description: 'Máš silnú orientáciu na excelenciu. Máš vysoké štandardy, si vytrvalý/vytrvalá a neustále sa snažíš zlepšovať.',
            strengths: [
                'Vysoké štandardy kvality',
                'Silná adaptabilita a flexibilita',
                'Vytrvalosť pri náročných úlohách',
                'Neustále hľadanie zlepšení'
            ],
            developmentAreas: [
                'Dávaj si pozor na vyhorenie - excelenciu treba vyvážiť odpočinkom',
                'Neboj sa delegovať, nie všetko musí byť dokonalé'
            ]
        }
    } else if (overallScore >= 30) {
        return {
            level: 'Priemerná orientácia na excelenciu',
            description: 'Máš vyváženú orientáciu na excelenciu. V niektorých oblastiach máš vysoké štandardy, v iných si viac flexibilný/flexibilná.',
            strengths: [
                'Vyvážený prístup k výkonom',
                'Dokážeš rozlíšiť, kde je excelencia potrebná'
            ],
            developmentAreas: [
                'Identifikuj oblasti, kde by vyššie štandardy mohli priniesť väčší dopad',
                'Rozvíjaj adaptabilitu - buď otvorený/otvorená zmenám',
                'Pracuj na vytrvalosti pri náročných úlohách'
            ]
        }
    } else {
        return {
            level: 'Nižšia orientácia na excelenciu',
            description: 'Excelenciu nepovažuješ za prioritu. Môžeš byť viac orientovaný/orientovaná na efektivitu než na dokonalosť.',
            strengths: [
                'Pragmatický prístup',
                'Efektivita pred perfekcionizmom'
            ],
            developmentAreas: [
                'Rozvíjaj adaptabilitu - buď flexibilnejší/flexibilnejšia pri zmenách',
                'Skús si nastaviť vyššie štandardy v jednej oblasti',
                'Pracuj na vytrvalosti - úspech prichádza s časom',
                'Hľadaj príležitosti na učenie a zlepšovanie'
            ]
        }
    }
}
